import React from 'react';

import { logger } from '@/lib/logger';

import type {
  ComponentRenderProps,
  ViewComponent,
} from '../../types/dynamic_views';
import { CardComponent } from './components/CardComponent';
import { ChartComponent } from './components/ChartComponent';
import { ConversationStarterComponent } from './components/ConversationStarterComponent';
import { DefinitionListComponent } from './components/DefinitionListComponent';
import { DividerComponent } from './components/DividerComponent';
import { FlexComponent } from './components/FlexComponent';
import { GridComponent } from './components/GridComponent';
// Import all component implementations
import { HeadingComponent } from './components/HeadingComponent';
import { ListComponent } from './components/ListComponent';
import { ParagraphComponent } from './components/ParagraphComponent';
import { SectionComponent } from './components/SectionComponent';
import { SourceListComponent } from './components/SourceListComponent';
import { TableComponent } from './components/TableComponent';
import { TextComponent } from './components/TextComponent';

export const ComponentRenderer: React.FC<ComponentRenderProps> = ({
  component,
  onInteraction,
  className = '',
  /**
   * Whether a proposal creation is in progress. When true, disables interactive
   * components to prevent double-submission.
   * @default false (not composing)
   */
  isComposing,
}) => {
  const handleComponentInteraction = (
    interactionType: string,
    data: Record<string, unknown>
  ) => {
    if (onInteraction) {
      onInteraction(interactionType, data);
    }
  };

  const renderComponent = (): React.ReactNode => {
    const { type, children } = component;

    // Common props passed to all components
    const commonProps = {
      component,
      onInteraction: handleComponentInteraction,
      className,
      isComposing,
    };

    switch (type) {
      // Text Components
      case 'heading':
        return <HeadingComponent {...commonProps} />;
      case 'text':
        return <TextComponent {...commonProps} />;
      case 'paragraph':
        return <ParagraphComponent {...commonProps} />;

      // Layout Components
      case 'section':
        return (
          <SectionComponent {...commonProps}>
            {renderChildren(children)}
          </SectionComponent>
        );
      case 'grid':
        return (
          <GridComponent {...commonProps}>
            {renderChildren(children)}
          </GridComponent>
        );
      case 'flex':
        return (
          <FlexComponent {...commonProps}>
            {renderChildren(children)}
          </FlexComponent>
        );
      case 'card':
        return (
          <CardComponent {...commonProps}>
            {renderChildren(children)}
          </CardComponent>
        );
      case 'divider':
        return <DividerComponent {...commonProps} />;

      // Data Components
      case 'table':
        return <TableComponent {...commonProps} />;
      case 'list':
        return <ListComponent {...commonProps} />;
      case 'definition_list':
        return <DefinitionListComponent {...commonProps} />;
      case 'source_list':
        return <SourceListComponent {...commonProps} />;

      // Visualization Components
      case 'chart':
        return <ChartComponent {...commonProps} />;

      case 'conversation_starter':
        // Validation delegated to ConversationStarterComponent
        logger.debug('ComponentRenderer: rendering conversation_starter', {
          component_id: component.id,
        });
        return (
          <ConversationStarterComponent
            {...commonProps}
            isComposing={isComposing}
          />
        );

      default:
        return (
          <div className='border border-utility-error-300 bg-utility-error-50 p-4 rounded'>
            <div className='text-utility-error-800 font-medium'>
              Unknown Component Type
            </div>
            <div className='text-utility-error-600 text-sm mt-1'>
              Component type "{type}" is not recognized. Available types:
              heading, text, paragraph, section, grid, flex, card, divider,
              table, list, definition_list, source_list, conversation_starter,
              chart.
            </div>
            <details className='mt-2 text-xs'>
              <summary className='cursor-pointer text-utility-error-700'>
                Component Data
              </summary>
              <pre className='mt-1 bg-utility-error-100 p-2 rounded overflow-auto'>
                {JSON.stringify(component, null, 2)}
              </pre>
            </details>
          </div>
        );
    }
  };

  const renderChildren = (children?: ViewComponent[]): React.ReactNode => {
    if (!children || !Array.isArray(children)) {
      return null;
    }

    return children.map((child, index) => (
      <ComponentRenderer
        key={child.id || `${child.type}-${index}`}
        component={child}
        onInteraction={onInteraction}
        isComposing={isComposing}
      />
    ));
  };

  // Synchronous render error handler
  // Note: This try-catch only handles errors during initial render.
  // It does NOT catch async errors or errors in event callbacks.
  // For comprehensive error handling, child components like ManifestValueLabel
  // implement proper React ErrorBoundary components.
  try {
    return (
      <div
        className='dynamic-component'
        data-component-type={component.type}
        data-component-id={component.id}
      >
        {renderComponent()}
      </div>
    );
  } catch (error) {
    console.error('Error rendering component:', error, component);

    return (
      <div className='border border-utility-error-300 bg-utility-error-50 p-4 rounded'>
        <div className='text-utility-error-800 font-medium'>
          Component Render Error
        </div>
        <div className='text-utility-error-600 text-sm mt-1'>
          We couldn't render the {component.type} component. Try refreshing the
          page.
        </div>
        <details className='mt-2 text-xs'>
          <summary className='cursor-pointer text-utility-error-700'>
            Component Data
          </summary>
          <pre className='mt-1 bg-utility-error-100 p-2 rounded overflow-auto'>
            {JSON.stringify(component, null, 2)}
          </pre>
        </details>
      </div>
    );
  }
};

export default ComponentRenderer;
